/**
 * The add-connection form's decisions, kept out of the component so they can be tested without a
 * browser.
 *
 * A connection is created against a provider the platform has registered, with an auth method
 * and scopes that provider actually offers. Letting a person type a provider id or a free-form
 * scope would mean the request fails at the hub, or worse, asks for a scope the provider never
 * granted.
 */
import { canonicalIntegrationTypes, type IntegrationType } from "@zworks/console-contracts";

export type AddConnectionDraft = {
  /** The provider id, from the registered list. */
  integrationType: string;
  name: string;
  authMethod: string;
};

/** The provider a draft names, or undefined when the id is not one the platform registers. */
export function providerFor(integrationType: string): IntegrationType | undefined {
  return canonicalIntegrationTypes.find((provider) => provider.id === integrationType);
}

/**
 * The scopes a new connection asks for: the read side of what the provider offers.
 *
 * A connection that starts with write scopes is one whose blast radius nobody chose. Write
 * scopes are granted afterwards, through the approval the Policies tab describes.
 */
export function defaultScopes(provider: IntegrationType): string[] {
  const read = provider.supportedScopes.filter((scope) => /(^|:)read$/.test(scope));
  return read.length > 0 ? read : provider.supportedScopes.slice(0, 1);
}

/** Whether the draft can be submitted, and why not when it cannot. */
export function checkDraft(draft: AddConnectionDraft): string | null {
  const provider = providerFor(draft.integrationType);
  if (!provider) {
    return "Choose a provider the platform supports.";
  }
  if (draft.name.trim() === "") {
    return "Give the connection a name you will recognise later.";
  }
  if (!provider.authMethods.includes(draft.authMethod as never)) {
    return "Choose how the connection authenticates.";
  }
  return null;
}

/** The request body for the draft, or null when the draft is not submittable. */
export function draftToRequest(draft: AddConnectionDraft, idempotencyKey: string) {
  const provider = providerFor(draft.integrationType);
  if (!provider || checkDraft(draft) !== null) {
    return null;
  }
  return {
    integrationType: provider.id,
    name: draft.name.trim(),
    authMethod: draft.authMethod,
    credentialRef: null,
    scopes: defaultScopes(provider),
    idempotencyKey,
  };
}
